import { Layout, Form } from '@shopify/polaris';
import type { ClientApplication } from '@shopify/app-bridge';
import Firebase from '../../contexts/firebase';
import { useCallback, useContext, useState } from 'react';
import { useNonNullableContext } from '../../hooks/useNonNullableContext';
import { AppConfigContext } from '../../contexts/AppConfigContext/AppConfigContext';
import { ToastContext } from '../../contexts/ToastContext';
import { Display } from './Display';
import { Form as ConfigurationForm } from './Form';
import { LoadingSpinnerCard } from '../Loading/LoadingSpinnerCard';
import { TCustomerConfig } from './types/TCustomerConfig';


export const Configuration = (props: { firebase: Firebase, app: ClientApplication }) => {
  const { isFetchingConfig, appConfig, saveAppConfig } = useNonNullableContext(AppConfigContext);
  const { dispatchToast } = useContext(ToastContext);
  const [draftCustomerTagConfig, setDraftCustomerTagConfig] = useState<TCustomerConfig["tags"]>({
    enabled: false,
    tagPrefixes: [],
  });
  const [draftCustomerMetafieldConfig, setDraftCustomerMetafieldConfig] = useState<
    TCustomerConfig["metafields"]
  >({
    enabled: false,
    allowedMetafields: [],
  });
  const [inEditMode, setInEditMode] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const customerConfig = appConfig.customers as TCustomerConfig;

  const goToEditMode = useCallback((editMode: boolean) => {
    if (editMode === true) {
      setDraftCustomerTagConfig(customerConfig.tags as TCustomerConfig["tags"]);
      setDraftCustomerMetafieldConfig(customerConfig.metafields as TCustomerConfig["metafields"]);
    }

    setInEditMode(editMode);
  }, [customerConfig]);

  // set title and description
  const title = "Customer data configuration";
  const description = "What customer data do you want to update with the app? This is needed to validate the input.";

  const handleTagPropertyUpdated = useCallback((key: string, value: any) => {
    setDraftCustomerTagConfig((currentData) => ({
      ...currentData,
      [key]: value,
    }));
  }, []);

  const handleMetafieldPropertyUpdated = useCallback((key: string, value: any) => {
    setDraftCustomerMetafieldConfig((currentData) => ({
      ...currentData,
      [key]: value,
    }));
  }, []);

  // set submit handler
  const handleSubmit = useCallback(async () => {
    try {
      if (draftCustomerTagConfig.enabled && draftCustomerTagConfig.tagPrefixes.length === 0) {
        throw new Error("You need at least one tag prefix when you enable tag prefixes.");
      }

      if (
        draftCustomerMetafieldConfig.enabled &&
        draftCustomerMetafieldConfig.allowedMetafields.length === 0
      ) {
        throw new Error(
          "You need at least one metafield when you enable the metafields option."
        );
      }

      setIsSubmitting(true);
      setHasError(false);

      await saveAppConfig('customers', {
        tags: draftCustomerTagConfig,
        metafields: draftCustomerMetafieldConfig,
      });

      setInEditMode(false);
    } catch (error) {
      if (error instanceof Error) {
        dispatchToast(error.message, { isError: true });
        setHasError(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    draftCustomerTagConfig,
    draftCustomerMetafieldConfig,
    saveAppConfig,
    dispatchToast,
  ]);

  const loadingMarkup = <LoadingSpinnerCard />;
  const contentMarkup = (
  <Form preventDefault onSubmit={handleSubmit}>
    {inEditMode ?
      <ConfigurationForm
        saveAppConfig={saveAppConfig}
        draftCustomerTagConfig={draftCustomerTagConfig}
        draftCustomerMetafieldConfig={draftCustomerMetafieldConfig}
        onTagPropertyUpdated={handleTagPropertyUpdated}
        onMetafieldPropertyUpdated={handleMetafieldPropertyUpdated}
        setInEditMode={goToEditMode}
        isSubmitting={isSubmitting}
        hasError={hasError}
      /> :
      <Display
        setInEditMode={goToEditMode}
        customerConfig={customerConfig}
      />
    }
  </Form>);

  return (
  <Layout.AnnotatedSection
    title={title}
    description={description}
  >
    {isFetchingConfig ? loadingMarkup : contentMarkup}
  </Layout.AnnotatedSection>
  );
};
